/**
 * The deadline a NEW sales document starts life with.
 *
 * Settings -> Invoices carries a payment term per document kind, and until this
 * file existed the editor never read it: the field opened empty and the operator
 * typed the date the settings screen already knew. React-free on purpose - the
 * editor holds the state, this holds the arithmetic, and the arithmetic is what
 * is worth a test.
 *
 * The same numbers are applied a second time on the server (see the create
 * routes under `modules/crm-sales`), which is deliberate rather than duplicated:
 * the server has to date a document that arrives from anywhere, and the screen
 * has to SHOW the date before it is saved. What matters is that both read the
 * one setting and reach the same answer.
 */

import type { DocNumberType, NumberingConfig } from "../../../lib/sales";
import type { DocKind } from "./config";

/**
 * The settings key that carries each kind's term, or null for a kind that has
 * no deadline of its own. A credit note corrects an invoice and inherits that
 * invoice's term, so it never asks; the editor draws no date field for it.
 */
const TERM_KEY: Record<DocKind, DocNumberType | null> = {
  invoices: "invoice",
  estimates: "estimate",
  proposals: "proposal",
  "credit-notes": null,
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** A day count worth applying: a whole number of days, zero included. Zero is a
 *  real setting ("due on issue") and must not be read as "unset". */
function isDayCount(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && Number.isInteger(v) && v >= 0;
}

/**
 * The configured term for a kind, or null when the settings have not answered
 * yet or the kind has no term.
 *
 * `dueDays` is consulted only for the invoice, and only as a fallback: it is the
 * pre-split single field, it has always meant the invoice term, and a server old
 * enough to send it alone knows nothing about the other kinds.
 */
export function termDays(cfg: NumberingConfig | null, kind: DocKind): number | null {
  const key = TERM_KEY[kind];
  if (!cfg || !key) return null;
  const byType = (cfg.dueDaysByType ?? {}) as Partial<Record<DocNumberType, number>>;
  const own = byType[key];
  if (isDayCount(own)) return own;
  if (key === "invoice" && isDayCount(cfg.dueDays)) return cfg.dueDays;
  return null;
}

/**
 * `from` (YYYY-MM-DD) moved on by `days`, as YYYY-MM-DD. An empty or unreadable
 * `from` counts from today, which is what the server does.
 *
 * Built out of local date parts rather than by adding milliseconds: a term that
 * crosses the spring clock change would otherwise land on the day before the one
 * a person counting on a calendar arrives at.
 */
export function addDays(from: string, days: number): string {
  const base = from ? new Date(`${from}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
}

/**
 * The date the editor should offer for a new document, or "" when nothing is
 * known yet - an empty string leaves the field as the operator found it rather
 * than filling it with a guess.
 *
 * A client's own `paymentTermsDays` beats the global setting, and only on an
 * invoice: that column is a PAYMENT term, agreed with that company, and an offer
 * is not a bill. This is the same order the invoice create route applies, so the
 * date on screen is the date that would have been written had the field been
 * left empty.
 */
export function defaultDeadline(opts: {
  kind: DocKind;
  cfg: NumberingConfig | null;
  /** The picked client's own payment term, when they have one. */
  clientTermDays?: number | null;
  /** The document date the term counts from. Empty means today. */
  from?: string;
}): string {
  const own = opts.kind === "invoices" && isDayCount(opts.clientTermDays) ? opts.clientTermDays : null;
  const days = own ?? termDays(opts.cfg, opts.kind);
  if (days == null) return "";
  return addDays(opts.from ?? "", days);
}
